import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import InstallerApp from './components/pwa/InstallerApp';
import { lazyPage, ChargementPage } from './components/layout/lazyPage';
import { useAuthStore } from './stores/auth.store';

// Layout
import MainLayout from './components/layout/MainLayout';

// Auth Components
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

// Pages
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'));
const ClubDashboardPage = lazyPage(() => import('./pages/ClubDashboardPage'));
const PlayerDashboardPage = lazyPage(() => import('./pages/PlayerDashboardPage'));
const GamesPage = lazyPage(() => import('./pages/GamesPage'));
const GamePlayPage = lazyPage(() => import('./pages/GamePlayPage'));
const ProgressionPage = lazyPage(() => import('./pages/ProgressionPage'));
const ProgramPage = lazyPage(() => import('./pages/ProgramPage'));
const SeancePhysiquePage = lazyPage(() => import('./pages/SeancePhysiquePage'));
const ChallengePage = lazyPage(() => import('./pages/ChallengePage'));
const FeedPage = lazyPage(() => import('./pages/FeedPage'));
const PostPage = lazyPage(() => import('./pages/PostPage'));
const SavedPostsPage = lazyPage(() => import('./pages/SavedPostsPage'));
const ActiveUsersPage = lazyPage(() => import('./pages/ActiveUsersPage'));
const SuggestionsPage = lazyPage(() => import('./pages/SuggestionsPage'));
const CompleteProfilePage = lazyPage(() => import('./pages/CompleteProfilePage'));
const CompleteCoachProfilePage = lazyPage(() => import('./pages/CompleteCoachProfilePage'));
const CoachTrainingsPage = lazyPage(() => import('./pages/CoachTrainingsPage'));
const TrainingDetailPage = lazyPage(() => import('./pages/TrainingDetailPage'));
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'));
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'));
const MarketplacePage = lazyPage(() => import('./pages/MarketplacePage'));
import AdminRoute from './components/admin/AdminRoute';
import AdminLayout from './components/admin/AdminLayout';
const AdminLoginPage = lazyPage(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboardPage = lazyPage(() => import('./pages/admin/AdminDashboardPage'));
const AdminPlatformDashboardPage = lazyPage(() => import('./pages/admin/AdminPlatformDashboardPage'));
const AdminProductsPage = lazyPage(() => import('./pages/admin/AdminProductsPage'));
const AdminOrdersPage = lazyPage(() => import('./pages/admin/AdminOrdersPage'));
const AdminShippingPage = lazyPage(() => import('./pages/admin/AdminShippingPage'));
const AdminClubsPage = lazyPage(() => import('./pages/admin/AdminClubsPage'));
const AdminUsersPage = lazyPage(() => import('./pages/admin/AdminUsersPage'));
import ClubModuleRoute from './components/club/ClubModuleRoute';
const MemberSpacePage = lazyPage(() => import('./pages/MemberSpacePage'));
const JoinClubPage = lazyPage(() => import('./pages/JoinClubPage'));
const CoachesPage = lazyPage(() => import('./pages/CoachesPage'));
const CoachDetailPage = lazyPage(() => import('./pages/CoachDetailPage'));
const MessengerPage = lazyPage(() => import('./pages/MessengerPage'));
const MatchesPage = lazyPage(() => import('./pages/MatchesPage'));
const CreateMatchPage = lazyPage(() => import('./pages/CreateMatchPage'));
const MatchDetailPage = lazyPage(() => import('./pages/MatchDetailPage'));
const ProductDetailPage = lazyPage(() => import('./pages/ProductDetailPage'));
const CartPage = lazyPage(() => import('./pages/CartPage'));
const OrdersHistoryPage = lazyPage(() => import('./pages/OrdersHistoryPage'));
const PaymentReturnPage = lazyPage(() => import('./pages/PaymentReturnPage'));
const MessagesPage = lazyPage(() => import('./pages/MessagesPage'));
const NotificationsPage = lazyPage(() => import('./pages/NotificationsPage'));
const NotFoundPage = lazyPage(() => import('./pages/NotFoundPage'));

// Club Pages
const ClubMembersPage = lazyPage(() => import('./pages/club/ClubMembersPage'));
const ClubTeamsPage = lazyPage(() => import('./pages/club/ClubTeamsPage'));
const ClubTrainingsPage = lazyPage(() => import('./pages/club/ClubTrainingsPage'));
const ClubMatchesPage = lazyPage(() => import('./pages/club/ClubMatchesPage'));
const ClubFinancesPage = lazyPage(() => import('./pages/club/ClubFinancesPage'));
const ClubTicketingPage = lazyPage(() => import('./pages/club/ClubTicketingPage'));
const ClubCommunicationPage = lazyPage(() => import('./pages/club/ClubCommunicationPage'));
const ClubMediaPage = lazyPage(() => import('./pages/club/ClubMediaPage'));
const AmateurAuthPage = lazyPage(() => import('./pages/AmateurAuthPage'));
const FollowOnboardingPage = lazyPage(() => import('./pages/FollowOnboardingPage'));
const ClubPerformancePage = lazyPage(() => import('./pages/club/ClubPerformancePage'));
const ClubRecruitmentPage = lazyPage(() => import('./pages/club/ClubRecruitmentPage'));
const ClubAdministrationPage = lazyPage(() => import('./pages/club/ClubAdministrationPage'));
const ClubContractsPage = lazyPage(() => import('./pages/club/ClubContractsPage'));
const ClubMedicalPage = lazyPage(() => import('./pages/club/ClubMedicalPage'));
const ClubEquipmentPage = lazyPage(() => import('./pages/club/ClubEquipmentPage'));
const ClubFacilitiesPage = lazyPage(() => import('./pages/club/ClubFacilitiesPage'));
const ClubParentsPage = lazyPage(() => import('./pages/club/ClubParentsPage'));
const MyTrainingsPage = lazyPage(() => import('./pages/MyTrainingsPage'));
const ClubPhotographesPage = lazyPage(() => import('./pages/club/ClubPhotographesPage'));
// Photographes : inscription, tableau de bord et page publique d'achat.
const PhotographeInscriptionPage = lazyPage(() => import('./pages/photographe/PhotographeInscriptionPage'));
const PhotographeDashboardPage = lazyPage(() => import('./pages/photographe/PhotographeDashboardPage'));
const PhotographePublicPage = lazyPage(() => import('./pages/public/PhotographePublicPage'));

// Billetterie publique — hors application authentifiée, et embarquable en iframe.
const TicketPurchasePage = lazyPage(() => import('./pages/public/TicketPurchasePage'));
const TicketWidgetPage = lazyPage(() => import('./pages/public/TicketWidgetPage'));
const MyTicketPage = lazyPage(() => import('./pages/public/MyTicketPage'));

function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          duration: 4000,
        }}
      />
      <InstallerApp />

      <Suspense fallback={<ChargementPage />}>
      <Routes>
        {/* Billetterie publique.

            Hors de tout layout et de toute garde : un supporter n'a pas de
            compte, et ces pages sont aussi celles que les clubs embarquent dans
            un iframe sur leur propre site. Les placer sous MainLayout aurait
            collé notre barre de navigation dans leur page — et un ProtectedRoute
            aurait renvoyé leurs visiteurs sur notre écran de connexion. */}
        <Route path="/billet/:eventId" element={<TicketPurchasePage />} />
        <Route path="/billetterie/:cle" element={<TicketWidgetPage />} />
        <Route path="/mon-billet/:ticketId" element={<MyTicketPage />} />

        {/* Public Auth Routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginForm />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterForm />}
        />

        {/* Espace supporter : numéro de téléphone et code, sans e-mail. Une
            seule page pour l'inscription et la connexion — c'est le même
            geste, et les séparer ferait deux écrans quasi identiques. */}
        <Route
          path="/supporter"
          element={isAuthenticated ? <Navigate to="/feed" replace /> : <AmateurAuthPage />}
        />

        {/* Espace photographe : numéro et code, comme le supporter. */}
        <Route
          path="/photographe/inscription"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <PhotographeInscriptionPage />}
        />

        {/* Administration — espaces autonomes, hors navigation publique.
            Deux périmètres distincts : la plateforme (clubs, comptes) réservée au
            rôle admin, et la boutique ouverte à admin comme à shop_admin. */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Anciennes URL de la boutique, avant la séparation des deux espaces. */}
        <Route path="/admin/produits" element={<Navigate to="/admin/boutique/produits" replace />} />
        <Route
          path="/admin/commandes"
          element={<Navigate to="/admin/boutique/commandes" replace />}
        />

        <Route element={<AdminRoute area="shop" />}>
          <Route path="/admin/boutique" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="produits" element={<AdminProductsPage />} />
            <Route path="commandes" element={<AdminOrdersPage />} />
            <Route path="livraison" element={<AdminShippingPage />} />
          </Route>
        </Route>

        <Route element={<AdminRoute area="platform" />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminPlatformDashboardPage />} />
            {/* Seule voie de création d'un profil club. */}
            <Route path="clubs" element={<AdminClubsPage />} />
            <Route path="utilisateurs" element={<AdminUsersPage />} />
          </Route>
        </Route>

        {/* Activation de l'espace d'un membre : le code d'accès tient lieu
            d'identification, la page est donc ouverte sans session. */}
        <Route path="/rejoindre" element={<JoinClubPage />} />

        {/* Dashboards spécifiques - No Layout (no navbar/footer) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/club-dashboard" element={<ClubDashboardPage />} />
          <Route path="/player-dashboard" element={<PlayerDashboardPage />} />
          {/* Espace privé d'un membre du club, dont le contenu suit son rôle. */}
          <Route path="/mon-club" element={<MemberSpacePage />} />
        </Route>

        {/* Public Routes with Layout */}
        <Route path="/" element={<MainLayout />}>
          {/* Le fil est la page d'entrée : la racine y mène directement. */}
          <Route index element={<Navigate to="/feed" replace />} />
          
          {/* Public Feed Route */}
          <Route path="feed" element={<FeedPage />} />
          {/* Jeux de lecture du jeu, ouverts à tous comme le fil. */}
          <Route path="jeux" element={<GamesPage />} />
          <Route path="jeux/:gameId" element={<GamePlayPage />} />
          {/* Programmes guidés, défis physiques et radar du joueur : publics,
              seule la progression demande un compte. */}
          <Route path="programmes" element={<ProgressionPage />} />
          <Route path="programmes/:programId" element={<ProgramPage />} />
          {/* Séance physique personnalisée, lancée depuis le fil : publique,
              la session ajoute la prise en compte de la charge récente. */}
          <Route path="seance-physique" element={<SeancePhysiquePage />} />
          <Route path="defis/:challengeId" element={<ChallengePage />} />
          {/* Un post seul : où mène un lien partagé sur un autre réseau. */}
          <Route path="post/:id" element={<PostPage />} />
          <Route path="favoris" element={<SavedPostsPage />} />
          {/* Choisir qui suivre : ouvert après l'inscription, puis accessible
              à tout moment pour en ajouter. */}
          <Route path="suivre" element={<FollowOnboardingPage />} />
          {/* Liste complete des membres actifs, cible du « Voir tout » du fil.
              Publique comme le fil : l'endpoint /auth/active-users l'est aussi. */}
          <Route path="active-users" element={<ActiveUsersPage />} />
          {/* Suggestions : consultable sans compte, mais suivre et envoyer un
              message exigent une session — la page le gere au clic. */}
          <Route path="suggestions" element={<SuggestionsPage />} />
          {/* Page publique d'un photographe : on y achète ses photos sans compte. */}
          <Route path="photographes/:slug" element={<PhotographePublicPage />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="dashboard" element={<DashboardPage />} />
            {/* Compte : informations personnelles et mot de passe. */}
            <Route path="settings" element={<SettingsPage />} />
            <Route path="player/complete-profile" element={<CompleteProfilePage />} />
            {/* Le profil entraineur a ses propres champs (specialites, diplomes,
                tarif) : il ne partage pas le formulaire du joueur. */}
            <Route path="coach/complete-profile" element={<CompleteCoachProfilePage />} />
            {/* Seances organisees par l'entraineur, publiques ou privees. */}
            <Route path="coach/trainings" element={<CoachTrainingsPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="trainings" element={<MyTrainingsPage />} />
            {/* Detail d'une seance, ouvert depuis un message partage. */}
            <Route path="trainings/:trainingId" element={<TrainingDetailPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            {/* Tableau de bord du photographe ; la page renvoie les autres comptes. */}
            <Route path="photographe" element={<PhotographeDashboardPage />} />
            <Route path="messenger/:coachId" element={<MessengerPage />} />
            <Route path="profile/:id?" element={<ProfilePage />} />
            <Route path="orders" element={<OrdersHistoryPage />} />
            {/* Retour de la page de paiement PayTech (success_url / cancel_url). */}
            <Route path="commande/:id/paiement" element={<PaymentReturnPage />} />
            
            {/* Club Management Routes — ClubModuleRoute referme les pages dont le
                module a été désactivé : masquer l'entrée de menu ne suffit pas,
                l'URL reste tapable. Administration en est exclue par principe :
                c'est de là qu'on rallume les modules. */}
            <Route element={<ClubModuleRoute />}>
              <Route path="club/members" element={<ClubMembersPage />} />
              <Route path="club/teams" element={<ClubTeamsPage />} />
              <Route path="club/recrutement" element={<ClubRecruitmentPage />} />
              <Route path="club/trainings" element={<ClubTrainingsPage />} />
              <Route path="club/matches" element={<ClubMatchesPage />} />
              <Route path="club/finances" element={<ClubFinancesPage />} />
              <Route path="club/communication" element={<ClubCommunicationPage />} />
              <Route path="club/media" element={<ClubMediaPage />} />
              <Route path="club/performance" element={<ClubPerformancePage />} />
              <Route path="club/medical" element={<ClubMedicalPage />} />
              <Route path="club/equipment" element={<ClubEquipmentPage />} />
              <Route path="club/facilities" element={<ClubFacilitiesPage />} />
              <Route path="club/parents" element={<ClubParentsPage />} />
              <Route path="club/billetterie" element={<ClubTicketingPage />} />
            </Route>
            {/* Administration du club : hors filtrage par module (c'est de là
                qu'on les rallume), mais réservée à qui a la main sur le club. */}
            <Route element={<ClubModuleRoute capability="club:admin" />}>
              <Route path="club/administration" element={<ClubAdministrationPage />} />
            </Route>
            {/* Contrats et paie : une délégation d'administration y donne accès,
                sans pour autant ouvrir la fiche du club. */}
            {/* Photographes : qui voit les matchs voit qui peut les couvrir. */}
            <Route element={<ClubModuleRoute capability="matches:read" />}>
              <Route path="club/photographes" element={<ClubPhotographesPage />} />
            </Route>
            <Route element={<ClubModuleRoute capability="contracts:read" />}>
              <Route path="club/contracts" element={<ClubContractsPage />} />
            </Route>
          </Route>

          {/* Public Browse Routes */}
          <Route path="marketplace" element={<MarketplacePage />} />
          <Route path="marketplace/:id" element={<ProductDetailPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="coaches" element={<CoachesPage />} />
          <Route path="coaches/:id" element={<CoachDetailPage />} />
          <Route path="matches" element={<MatchesPage />} />
          <Route path="matches/create" element={<CreateMatchPage />} />
          <Route path="matches/:id" element={<MatchDetailPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      </Suspense>
    </>
  );
}

export default App;
